/**
 * <oppie-delivery-line> — de bezorgregel in de announcement-bar, de menu's en onder
 * de koopknop. De server rendert "Vóór 23:59 besteld, morgen in huis". Dit script
 * past de regel aan op de dag en het uur, zodat de belofte altijd klopt (ook als de
 * pagina uit de cache komt).
 *
 * Rekent in Europe/Amsterdam (de klok van het magazijn), net als de aftelling in de
 * winkelwagen (oppie-cart-delivery-countdown.js):
 * - vrijdag vóór de vrijdag-deadline (data-friday-cutoff-*, zaterdagbezorging) →
 *   template "before-friday", dag = zaterdag ("morgen");
 * - werkdag vóór de besteltijd → template "before", dag = eerstvolgende werkdag
 *   ("morgen", of de dagnaam, bv. "maandag" op vrijdag zonder zaterdagbezorging);
 * - anders (vrijdag na de deadline, weekend, feestdag) → template "later", dag =
 *   eerstvolgende werkdag. De vervoerder haalt 's ochtends op en bezorgt dezelfde
 *   dag, dus vrijdagavond, zaterdag of zondag besteld = maandag in huis.
 * Op zondag bezorgen we nooit.
 */
const TIME_ZONE = 'Europe/Amsterdam';
const DAY_MS = 24 * 60 * 60 * 1000;

/** @param {Date} date */
function warehouseClock(date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const get = (type) => parts.find((part) => part.type === type)?.value ?? '';
  return {
    isoDate: `${get('year')}-${get('month')}-${get('day')}`,
    minutesOfDay: Number(get('hour')) * 60 + Number(get('minute')),
  };
}

/**
 * @param {string} isoDate - YYYY-MM-DD
 * @param {number} days
 */
function addDays(isoDate, days) {
  return new Date(Date.parse(`${isoDate}T12:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);
}

class OppieDeliveryLine extends HTMLElement {
  connectedCallback() {
    const cutoff = parseInt(this.dataset.cutoffHour, 10) * 60 + parseInt(this.dataset.cutoffMinute, 10);
    if (Number.isNaN(cutoff)) return;
    this.holidays = new Set((this.dataset.holidays || '').split(/[\s,]+/).filter(Boolean));

    const fridayCutoff =
      parseInt(this.dataset.fridayCutoffHour, 10) * 60 + parseInt(this.dataset.fridayCutoffMinute, 10);

    const { isoDate: today, minutesOfDay } = warehouseClock(new Date());
    const saturday = addDays(today, 1);
    const fridayRoute =
      !Number.isNaN(fridayCutoff) &&
      new Date(`${today}T12:00:00Z`).getUTCDay() === 5 &&
      this.#isWorkingDay(today) &&
      !this.holidays.has(saturday);

    let template = this.dataset.tplLater;
    let deliveryDay = this.#nextWorkingDay(today);
    if (fridayRoute && minutesOfDay < fridayCutoff) {
      template = this.dataset.tplBeforeFriday;
      deliveryDay = saturday;
    } else if (!fridayRoute && this.#isWorkingDay(today) && minutesOfDay < cutoff) {
      template = this.dataset.tplBefore;
    }
    const dayLabel = this.#label(today, deliveryDay);
    if (!template || !dayLabel) return;

    // Templates komen uit de vertaalbestanden (vertrouwd, kunnen <strong> bevatten).
    this.innerHTML = template.replace('[day]', dayLabel);
  }

  /** @param {string} isoDate */
  #isWorkingDay(isoDate) {
    const weekday = new Date(`${isoDate}T12:00:00Z`).getUTCDay();
    return weekday !== 0 && weekday !== 6 && !this.holidays.has(isoDate);
  }

  /** @param {string} isoDate */
  #nextWorkingDay(isoDate) {
    let next = addDays(isoDate, 1);
    while (!this.#isWorkingDay(next)) next = addDays(next, 1);
    return next;
  }

  /**
   * @param {string} today
   * @param {string} deliveryDay
   */
  #label(today, deliveryDay) {
    if (deliveryDay === addDays(today, 1)) return this.dataset.labelTomorrow || '';
    try {
      return new Intl.DateTimeFormat(this.dataset.locale || document.documentElement.lang || 'nl', {
        weekday: 'long',
        timeZone: 'UTC',
      }).format(new Date(`${deliveryDay}T12:00:00Z`));
    } catch {
      return '';
    }
  }
}

if (!customElements.get('oppie-delivery-line')) {
  customElements.define('oppie-delivery-line', OppieDeliveryLine);
}
